import './styles.scss';

import { faChevronDown, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { LABELS_PNL_TRADES } from '..';
import { PNLContext } from '../../../../../../providers/pnl-provider';
import Utils from '../../../../../../utils/immersive_utils';
import { CheckLastUpdate } from '../../../../components/check-last-update';
import { BookErrorsMessage } from '../../book-errors-message';
import { ReorderPnlTableMenu } from '../../reorder-pnl-table-menu';

const { formatNumber } = Utils;

interface PnlTableProps {
  data: any;
  header: any[];
  lastUpdatePNL: string;
  newHeader: any[];
  bookErrors: any[];
}

export const PnlTable = ({ data, header, newHeader, lastUpdatePNL, bookErrors }: PnlTableProps) => {
  const [alertVisible, setAlertVisible] = useState(false);
  const {
    isFirstLoadData,
    filters: { expandableRows, expandableColums, setExpandableRows, setExpandableColums },
    sortPnlTable,
    setSortPnlTable,
  } = useContext(PNLContext);

  const LABELS_PNL_FOR_DISPLAY_TOTAL_ITEMS = Object.values(LABELS_PNL_TRADES).filter((label: any) =>
    ['Futures', 'Options', 'Spot'].includes(label),
  );

  const handleExpandable = useCallback(
    (id: string) => {
      setExpandableRows((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
      );
    },
    [setExpandableRows],
  );

  const renderDataLeft = (block: any[]) => {
    const renderChildrenTableLeft = (arr: any[], level: number) => {
      return arr.map((item, index) => {
        if (item.children && item.children.length) {
          return renderNodeItem(item, level, `${item.id}_${index}`);
        } else {
          return (
            <tr className={`level-${level}`} key={`${item.id}_${index}`}>
              <td style={{ width: '1000px' }} className={item.id === 'spot' ? 'spot' : ''}>
                <div
                  className="table-item"
                  style={{
                    paddingLeft: `${16 * level}px`,
                    width: '100%',
                  }}
                >
                  <span className="label first-label">
                    {item.label} {LABELS_PNL_FOR_DISPLAY_TOTAL_ITEMS.includes(item.label) && '(0)'}
                  </span>
                  <span className="label">{item.position}</span>
                </div>
              </td>
            </tr>
          );
        }
      });
    };

    const renderNodeItem = (item: any, level: number, key: string) => {
      return (
        <tr style={{ width: '100%' }} className={`level-${level}`} key={key}>
          <td style={{ paddingBottom: 0, width: '10000px' }}>
            <div className="cell-node">
              <div
                onClick={() => handleExpandable(item.id)}
                className="node-button"
                style={{
                  paddingLeft: `${16 * level}px`,
                }}
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`icon ${expandableRows.includes(item.id) && 'rotate'}`}
                />
                <span className="label first-label">
                  {item.label} {` (${item.totalItems})`}
                </span>
                <span className={`label ${item.position === 'position' ? 'label-position' : ''}`}>
                  {item.position}
                </span>
              </div>
              <div className={`items level-${level}`}>
                {expandableRows.includes(item.id) &&
                  item.children &&
                  renderChildrenTableLeft(item.children, level + 1)}
              </div>
            </div>
          </td>
        </tr>
      );
    };

    return renderChildrenTableLeft(block, 0);
  };

  const renderDataContent = (block: any[]) => {
    const items: any[] = [];

    const dataLoop = (item: any, parent: string[], level: number) => {
      items.push({
        id: item.id,
        label: item.label,
        parent,
        values: item.values,
        level: level,
        hasChildren: !!item.children && item.children.length,
      });

      if (item.children && item.children.length) {
        item.children.forEach((child: any) => {
          dataLoop(child, [...parent, item.id], level + 1);
        });
      }
    };

    block.forEach((item) => {
      dataLoop(item, [], 0);
    });

    return items.map((item, index) => {
      let render = true;

      item.parent.forEach((parent: any) => {
        if (!expandableRows.includes(parent)) {
          render = false;
        }
      });

      if (!render) return null;

      return (
        <tr className={`level-${item.level}`} key={`${item.id}__${index}`}>
          {item.values.map((val: any, i: number) => {
            const value = parseFloat(val);

            const canShow =
              newHeader.find((h) => h.label === header[i])?.items !== undefined ||
              expandableColums.find((expandableColumn) =>
                expandableColumn.items.find((item: string) => item === header[i]),
              );

            if (!canShow) {
              return null;
            }

            const formatValueForTable = (value: any, index: number) => {
              const isNegative = parseFloat(value) < 0;
              const absoluteValue = Math.abs(value);
              let decimalPlaces = 2;
              const instPVT1Index = 3;
              const instPVT2Index = 4;

              if ((index === instPVT1Index || index === instPVT2Index) && !isNaN(value)) {
                decimalPlaces = 4;
              }

              const formattedValue = formatNumber(absoluteValue, decimalPlaces);
              return isNegative ? `(${formattedValue})` : formattedValue;
            };

            const isChildrenColumn = expandableColums.find((expandableColumn) =>
              expandableColumn.items.find((item: string) => item === header[i]),
            );

            let className = `${isChildrenColumn ? 'children-column' : ''}`;

            if (!isNaN(value) && value < 0) {
              className += ' negative';
            }

            if (isNaN(value)) {
              return (
                <td key={`${val}_${i}`} className={className}>
                  {val}
                </td>
              );
            }

            return (
              <td key={`${val}_${i}`} className={className}>
                {formatValueForTable(value, i)}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  const renderHeader = (column: any) => {
    const newHeaderColumn = newHeader.find((h) => h.label === column);

    if (newHeaderColumn?.items && newHeaderColumn?.items.length > 0) {
      const IsShowing = !!expandableColums.find((col) => col.id === newHeaderColumn.id);

      return (
        <th
          key={column}
          style={{ width: '88px' }}
          className="expandable-column"
          onClick={() => {
            setExpandableColums((prev: any) => {
              const prevItem = prev.find((prevItem: any) => prevItem.id === newHeaderColumn.id);

              if (prevItem) {
                return prev.filter((item: any) => prevItem.id !== item.id);
              }

              return [...prev, newHeaderColumn];
            });
          }}
        >
          {column}{' '}
          <FontAwesomeIcon icon={faChevronRight} className={`icon ${IsShowing ? 'rotate' : ''}`} />
        </th>
      );
    } else {
      const canShow =
        newHeader.find((h) => h.label === column)?.items !== undefined ||
        expandableColums.find((expandableColumn) =>
          expandableColumn.items.find((item: string) => item === column),
        );

      if (canShow) {
        return (
          <th key={column} style={{ width: '88px' }}>
            {column}
          </th>
        );
      }
    }
  };

  useEffect(() => {
    if (!isFirstLoadData.current) return;

    if (!data || data.length === 0) return;

    const maxLevel = 2;
    const expandableFirstInstrument = Utils.expandableArrReportsForLevel(data[0], maxLevel);

    setExpandableRows([...expandableFirstInstrument]);
    isFirstLoadData.current = false;
  }, [data]);

  const consoleError = console.error;

  console.error = (err: any) => {
    if (err && (typeof err !== 'string' || !err.includes('cannot appear as a child'))) {
      consoleError(err);
    }
  };

  const hasData = data && data.length > 0 && data[0]?.children.length > 0;

  const renderReorderPnlTableMenu = useMemo(() => {
    if (data.length > 0 && data[0].children.length > 0) {
      return <ReorderPnlTableMenu sortTable={sortPnlTable} setSortTable={setSortPnlTable} />;
    }
    return null;
  }, [data, sortPnlTable]);

  return (
    <div className="pnl-table-container">
      {hasData && <CheckLastUpdate lastUpdate={lastUpdatePNL} />}
      <BookErrorsMessage
        alertVisible={alertVisible}
        setAlertVisible={setAlertVisible}
        bookErrors={bookErrors}
      />
      {hasData && (
        <div style={{ display: 'flex' }}>
          <div className="pnl-table">
            <div className="left-table">
              <table>
                <thead>
                  <tr>
                    <th key="instrument">Instrument</th>
                  </tr>
                </thead>
                {data?.length > 0 && <tbody>{renderDataLeft(data)}</tbody>}
              </table>
            </div>

            <div className="container-content-table">
              <table className="content-table">
                <thead>
                  <tr>{header.map((column) => renderHeader(column))}</tr>
                </thead>
                {data?.length > 0 && <tbody>{renderDataContent(data)}</tbody>}
              </table>
            </div>
          </div>
          <div className="risk-reorder-pnl-table-container">{renderReorderPnlTableMenu}</div>
        </div>
      )}
      {!hasData && <div className="empty-body">{`You don't have any positions`}</div>}
    </div>
  );
};
